import { useQuery, useLazyQuery } from '@apollo/client/react';
import React, { useEffect, useState } from 'react';
import { Textfield } from '../../../_common/Textfield/Textfield';
import Modal from 'react-bootstrap/Modal';
import { GET_USER_FILTERED, GET_RECIPIENT_DETAILS } from '../../../../graphql/client/queries';
import { GetUserFiltered, GetRecipientDetails } from '../../../../types/schemaTypes';
import { CustomButton, Loading } from '../../../_common';
import Chips from '../../../_common/Chips/Chips';
import './ConfigureEmailRecipientsModal.scss';
import { MultiSelectDropdownV2 } from '../../../_common/MultiSelectDropdown/MultiSelectDropdownV2';
// import { ValueType } from 'react-select/src/types';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

interface OptionType {
    label: string;
    value: string;
}
//email: 'react@gmail.com', valid: true, key: '1'
interface EmailChipType {
    email: string;
    valid: boolean;
    key: string;
    readOnly?: boolean;
}
interface ConfigureEmailRecipientsModalProps {
    showAddConfigureRecipientsModal: boolean;
    handleOnClose: (modalType) => void;
    //handleOnSave: (userIds: string, userNames: string) => {};
    handleOnSave: (input: unknown) => void;
    JobId: number;
    // recipientDetailsData: RecipientDetails;
    mutationLoading: boolean;
    mutationError: string | undefined;
}
const pattern = new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/);

const buildExternalEmailChips = (
    emails: (string | null | undefined)[],
    regionEmail?: string | null,
): EmailChipType[] => {
    const chips: EmailChipType[] = [];
    const trimmedRegion = regionEmail?.trim();
    const addedEmails = new Set<string>();

    if (trimmedRegion) {
        addedEmails.add(trimmedRegion.toLowerCase());
        chips.push({
            email: trimmedRegion,
            valid: true,
            key: 'region-email',
            readOnly: true,
        });
    }

    emails.forEach(email => {
        const trimmed = email?.trim();
        const normalized = trimmed?.toLowerCase();
        if (trimmed && normalized && !addedEmails.has(normalized)) {
            addedEmails.add(normalized);
            chips.push({
                email: trimmed,
                valid: true,
                key: `email-${chips.length}`,
                readOnly: false,
            });
        }
    });

    return chips;
};

const ConfigureEmailRecipientsModal = ({
    showAddConfigureRecipientsModal,
    handleOnClose,
    handleOnSave,
    JobId,
    mutationLoading,
    mutationError,
}: ConfigureEmailRecipientsModalProps): React.ReactElement => {
    const users: OptionType[] = [];
    const tempUsers: OptionType[] = [];
    const initialSelectedUsers: OptionType[] = [];
    const usersStageSheetEmail: OptionType[] = [];
    const tempUsersStageSheetEmail: OptionType[] = [];
    ///let initialSelectedStageSheetEmail: ValueType<OptionType>;
    const initialSelectedStageSheetEmail: OptionType[] = [];

    const usersStageStartEndEmail: OptionType[] = [];
    const tempUsersStageStartEndEmail: OptionType[] = [];
    const initialSelectedStageStartEndEmail: OptionType[] = [];
    const initialStageStartEndChipEmail: EmailChipType[] = [];
    const initialStageSheetEmailChipEmail: EmailChipType[] = [];
    const initialJobEventsEmailChipEmail: EmailChipType[] = [];

    const [selectedJobEventsEmailTextVal, setSelectedJobEventsEmailTextVal] = useState(initialJobEventsEmailChipEmail);

    const usersJobEventsEmail: OptionType[] = [];
    const tempUsersJobEventsEmail: OptionType[] = [];
    const initialSelectedJobEventsEmail: OptionType[] = [];
    const [selectedJobEventsEmail, setSelectedJobEventsEmail] = useState(initialSelectedJobEventsEmail);

    const input = {
        JobId: 1,
        RecipientsDetails: [{ userId: 0, alertType: '', userEmail: '', isExternal: false }],
    };

    const [selectedUserIds, setSelectedUserIds] = useState('');
    const [recipientsDetailsInput, setRecipientsDetailsInput] = useState([
        { userId: 0, alertType: '', userEmail: '', isExternal: false },
    ]);
    const [selectedUsers, setSelectedUsers] = useState(initialSelectedUsers);
    const [selectedStageSheetEmail, setSelectedStageSheetEmail] = useState(initialSelectedStageSheetEmail);
    const [selectedStageStartEndEmail, setSelectedStageStartEndEmail] = useState(initialSelectedStageStartEndEmail);
    const [selectedStageSheetEmailTextVal, setSelectedStageSheetEmailTextVal] = useState(
        initialStageSheetEmailChipEmail,
    );
    const [selectedStageStartEndEmailTextVal, setSelectedStageStartEndEmailTextVal] = useState(
        initialStageStartEndChipEmail,
    );
    const [regionEmail, setRegionEmail] = useState<string | null>(null);

    const preserveRegionEmailChips = (chips: EmailChipType[]): EmailChipType[] => {
        const editableEmails = chips.filter(chip => !chip.readOnly).map(chip => chip.email);
        return buildExternalEmailChips(editableEmails, regionEmail);
    };

    //const [inputs, setInputs] = useState(input);

    const [
        getJobId,
        { data: recipientDetailsData, loading: getRecipientDetailsLoading },
    ] = useLazyQuery<GetRecipientDetails>(GET_RECIPIENT_DETAILS, { fetchPolicy: 'network-only' });

    const { data, loading } = useQuery<GetUserFiltered>(GET_USER_FILTERED, {
        variables: { input: '' },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (showAddConfigureRecipientsModal) {
            setRegionEmail(null);
            setSelectedStageSheetEmailTextVal([]);
            setSelectedStageStartEndEmailTextVal([]);
            setSelectedJobEventsEmailTextVal([]);
        }
    }, [showAddConfigureRecipientsModal]);

    useEffect(() => {
        if (!loading && data) {
            getJobId({
                variables: {
                    jobId: JobId,
                    notificationType: 'All',
                },
            });
        }
    }, [loading, data, JobId, getJobId]);

    useEffect(() => {
        if (!recipientDetailsData?.getRecipientDetails) {
            return;
        }

        const recipientDetailsList =
            recipientDetailsData.getRecipientDetails.recipientDetails?.filter(
                (item): item is NonNullable<typeof item> => item != null,
            ) ?? [];
        const regionEmailFromApi = recipientDetailsData.getRecipientDetails.regionEmail;
        setRegionEmail(regionEmailFromApi ?? null);

        const stageAlert = recipientDetailsList
            .filter(item => item?.alertType === 'StageAlerts')
            .map(a => a?.userId);

        const normalizedRegionEmail = regionEmailFromApi?.trim().toLowerCase();
        const stageSheetEmailsExtEmail = recipientDetailsList
            .filter(item => item?.alertType === 'StageSheetEmails' && item?.isExternal === true)
            .map(a => a?.email || a?.name)
            .filter(email => {
                const trimmed = email?.trim();
                return trimmed && (!normalizedRegionEmail || trimmed.toLowerCase() !== normalizedRegionEmail);
            });

        const stageStartEndEmailsExtEmail = recipientDetailsList
            .filter(item => item?.alertType === 'StageStartEndEmails' && item?.isExternal === true)
            .map(a => a?.email || a?.name);

        const stageSheetEmails = recipientDetailsList
            .filter(item => item?.alertType === 'StageSheetEmails' && item?.isExternal === false)
            .map(a => a?.userId);

        const stageStartEndEmails = recipientDetailsList
            .filter(item => item?.alertType === 'StageStartEndEmails' && item?.isExternal === false)
            .map(a => a?.userId);

        const jobEventsEmail = recipientDetailsList
            .filter(item => item?.alertType === 'JobLogEmails' && item?.isExternal === false)
            .map(a => a?.userId);

        const jobEventsEmailsExtEmail = recipientDetailsList
            .filter(item => item?.alertType === 'JobLogEmails' && item?.isExternal === true)
            .map(a => a?.email || a?.name);

        setSelectedStageSheetEmailTextVal(buildExternalEmailChips(stageSheetEmailsExtEmail, null));
        setSelectedStageStartEndEmailTextVal(
            buildExternalEmailChips(stageStartEndEmailsExtEmail, regionEmailFromApi),
        );
        setSelectedJobEventsEmailTextVal(
            buildExternalEmailChips(jobEventsEmailsExtEmail, regionEmailFromApi),
        );

        if (recipientDetailsList.length > 0) {
            for (let i = 0; i < stageAlert.length; i++) {
                if (users.find(l => l?.value === stageAlert[i]?.toString()))
                    var tmpStageAlert = users.find(l => l?.value === stageAlert[i]?.toString());

                if (tmpStageAlert && tmpStageAlert.label !== null)
                    tempUsers.push({
                        value: '' + stageAlert[i] + '',
                        label: '' + tmpStageAlert.label + '',
                    });
            }
            setSelectedUsers(tempUsers);

            for (let i = 0; i < jobEventsEmail.length; i++) {
                if (users.find(l => l?.value === jobEventsEmail[i]?.toString()))
                    var tmpEventsEmail = users.find(l => l?.value === jobEventsEmail[i]?.toString());

                if (tmpEventsEmail && tmpEventsEmail.label !== null)
                    tempUsersJobEventsEmail.push({
                        value: '' + jobEventsEmail[i] + '',
                        label: '' + tmpEventsEmail.label + '',
                    });
            }
            setSelectedJobEventsEmail(tempUsersJobEventsEmail);

            for (let i = 0; i < stageSheetEmails.length; i++) {
                if (usersStageSheetEmail.find(l => l?.value === stageSheetEmails[i]?.toString()))
                    var tmpStageSheetEmail = usersStageSheetEmail.find(
                        l => l?.value === stageSheetEmails[i]?.toString(),
                    );

                if (tmpStageSheetEmail && tmpStageSheetEmail.label !== null)
                    tempUsersStageSheetEmail.push({
                        value: '' + stageSheetEmails[i] + '',
                        label: '' + tmpStageSheetEmail.label + '',
                    });
            }
            setSelectedStageSheetEmail(tempUsersStageSheetEmail);

            for (let i = 0; i < stageStartEndEmails.length; i++) {
                if (usersStageStartEndEmail.find(l => l?.value === stageStartEndEmails[i]?.toString()))
                    var tmpStageStartEndEmails = usersStageStartEndEmail.find(
                        l => l?.value === stageStartEndEmails[i]?.toString(),
                    );

                if (tmpStageStartEndEmails && tmpStageStartEndEmails.label !== null)
                    tempUsersStageStartEndEmail.push({
                        value: '' + stageStartEndEmails[i] + '',
                        label: '' + tmpStageStartEndEmails.label + '',
                    });
            }
            setSelectedStageStartEndEmail(tempUsersStageStartEndEmail);
        }
    }, [recipientDetailsData, data]);
    if (data?.getUserFiltered && data.getUserFiltered.length !== 0) {
        data.getUserFiltered
            .filter(s => s?.status === 'Active')
            .forEach((element) => {
                if (element != null && element.id != null && element.name != null) {
                    users.push({
                        value: String(element.id),
                        label: element.name,
                    });
                    usersStageSheetEmail.push({
                        value: String(element.id),
                        label: element.name,
                    });
                    usersStageStartEndEmail.push({
                        value: String(element.id),
                        label: element.name,
                    });
                    usersJobEventsEmail.push({
                        value: String(element.id),
                        label: element.name,
                    });
                }
            });
    }

    const changeStageSheetEmailTextVal = (data): void => {
        setSelectedStageSheetEmailTextVal(data);
    };
    const changeStageStartEndEmailTextVal = (data): void => {
        setSelectedStageStartEndEmailTextVal(preserveRegionEmailChips(data));
    };
    const changeJobEventEmailTextVal = (data): void => {
        setSelectedJobEventsEmailTextVal(preserveRegionEmailChips(data));
    };
    const onSave = (): void => {
        for (let i = 0; i < selectedJobEventsEmail.length; i++) {
            input.RecipientsDetails.push({
                userId: parseInt(selectedJobEventsEmail[i].value),
                alertType: 'JobLogEmails',
                userEmail: '',
                isExternal: false,
            });
        }

        for (let i = 0; i < selectedUsers.length; i++) {
            input.RecipientsDetails.push({
                userId: parseInt(selectedUsers[i].value),
                alertType: 'StageAlerts',
                userEmail: '',
                isExternal: false,
            });
        }

        for (let i = 0; i < selectedStageSheetEmail.length; i++) {
            input.RecipientsDetails.push({
                userId: parseInt(selectedStageSheetEmail[i].value),
                alertType: 'StageSheetEmails',
                userEmail: '',
                isExternal: false,
            });
        }
        for (let i = 0; i < selectedStageStartEndEmail.length; i++) {
            input.RecipientsDetails.push({
                userId: parseInt(selectedStageStartEndEmail[i].value),
                alertType: 'StageStartEndEmails',
                userEmail: '',
                isExternal: false,
            });
        }

        if (selectedStageSheetEmailTextVal && selectedStageSheetEmailTextVal.length > 0) {
            for (let i = 0; i < selectedStageSheetEmailTextVal.length; i++) {
                input.RecipientsDetails.push({
                    userId: 0,
                    alertType: 'StageSheetEmails',
                    userEmail: selectedStageSheetEmailTextVal[i].email,
                    isExternal: true,
                });
            }
        }

        if (selectedStageStartEndEmailTextVal && selectedStageStartEndEmailTextVal.length > 0) {
            for (let i = 0; i < selectedStageStartEndEmailTextVal.length; i++) {
                if (selectedStageStartEndEmailTextVal[i].readOnly) {
                    continue;
                }
                input.RecipientsDetails.push({
                    userId: 0,
                    alertType: 'StageStartEndEmails',
                    userEmail: selectedStageStartEndEmailTextVal[i].email,
                    isExternal: true,
                });
            }
        }

        if (selectedJobEventsEmailTextVal && selectedJobEventsEmailTextVal.length > 0) {
            for (let i = 0; i < selectedJobEventsEmailTextVal.length; i++) {
                if (selectedJobEventsEmailTextVal[i].readOnly) {
                    continue;
                }
                input.RecipientsDetails.push({
                    userId: 0,
                    alertType: 'JobLogEmails',
                    userEmail: selectedJobEventsEmailTextVal[i].email,
                    isExternal: true,
                });
            }
        }
        input.JobId = JobId;
        input.RecipientsDetails.shift();
        handleOnSave(input);
        // handleOnClose('AddConfigureRecipients');
    };

    const onClickClose = () => {
        handleOnClose('AddConfigureRecipients');
    };

    const setSaveButtonStatus = (): boolean => {
        // const isUserNamesValid = selectedUserIds?.trim().length > 0 ? true : false;
        // const isFormValid = mutationLoading;
        return mutationLoading;
        //return false;
    };

    const onStageAlertChange = (selectedUser: OptionType): void => {
        const userIds: string[] = [];
        if (selectedUser && selectedUser !== null && Array.isArray(selectedUser)) {
            for (let i = 0; i < selectedUser.length; i++) {
                userIds.push(selectedUser[i].value);
            }
            const numbersCombined = [...recipientsDetailsInput];
            setRecipientsDetailsInput(numbersCombined);
            setSelectedUsers(selectedUser);
            //  setTempSSelectedUsers(selectedUser);
            setSelectedUserIds(userIds.toString());
        }
        //setInputs(...input, input);
    };
    const onStageSheetEmailChange = (selectedUser: OptionType): void => {
        if (selectedUser && selectedUser !== null && Array.isArray(selectedUser)) {
            setSelectedStageSheetEmail(selectedUser);
        }

        //setInputs(...input, input);
    };
    const onStageStartEndEmail = (selectedUser: OptionType): void => {
        if (selectedUser && selectedUser !== null && Array.isArray(selectedUser)) {
            setSelectedStageStartEndEmail(selectedUser);
        }
    };
    const onJobEventEmail = (selectedJobEventsEmail: OptionType): void => {
        if (selectedJobEventsEmail && selectedJobEventsEmail !== null && Array.isArray(selectedJobEventsEmail)) {
            setSelectedJobEventsEmail(selectedJobEventsEmail);
        }
    };
    return (
        <Modal
            show={showAddConfigureRecipientsModal}
            onHide={onClickClose}
            dialogClassName="ConfigureEmailRecipientsModal-Container"
        >
            {getRecipientDetailsLoading && (
                <span className="AddStageModal-Loading">
                    <Loading />
                </span>
            )}

            <Modal.Header className="ConfigureEmailRecipientsModal-Header">
                <Modal.Title className="ConfigureEmailRecipientsModal-Header-Container">
                    <button className="material-icons ConfigureEmailRecipientsModal-X-Button" onClick={onClickClose}>
                        close
                    </button>
                    <div className="ConfigureEmailRecipientsModal-Title">Configure Email Recipients</div>
                    <div className="ConfigureEmailRecipientsModal-ErrorMessage">{mutationError}</div>
                </Modal.Title>
            </Modal.Header>
            <div className="ConfigureEmailRecipientsModal-Container">
                <Row className="ConfigureEmailRecipientsModal-Upper-TextField-Container">
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Alert Recipients</label> */}
                        <MultiSelectDropdownV2
                            dropdownOptions={users}
                            label="Inventory Alert Recipients"
                            onChange={(value: OptionType): void => {
                                onStageAlertChange(value);
                            }}
                            key={`my_unique_select_key__${JSON.stringify(selectedUsers)}`}
                            value={selectedUsers}
                        />
                    </Col>
                </Row>
                <Row className="ConfigureEmailRecipientsModal-Upper-TextField-Container">
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Sheet Email Recipients (SBX users)</label> */}
                        <MultiSelectDropdownV2
                            dropdownOptions={usersStageSheetEmail}
                            label="Stage Sheet Email Recipients (SBX users)"
                            onChange={(value: OptionType): void => {
                                onStageSheetEmailChange(value);
                            }}
                            key={`my_unique_select_key__${JSON.stringify(selectedStageSheetEmail)}`}
                            value={selectedStageSheetEmail}
                        />
                    </Col>
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Sheet Email Recipients (non-SBX users)</label> */}
                        <div style={{ marginTop: '11px' }}>
                            <Chips
                                key={`stage-sheet-chips-${selectedStageSheetEmailTextVal.map(c => c.email).join(',')}`}
                                chips={selectedStageSheetEmailTextVal}
                                placeholder="Add email"
                                title="Stage Sheet Email Recipients (non-SBX users)"
                                save={changeStageSheetEmailTextVal}
                                pattern={pattern}
                                required={false}
                                limit="500"
                                limitNotification={data => console.log('limit notification', data)}
                            ></Chips>
                        </div>
                    </Col>
                </Row>
                <Row className="ConfigureEmailRecipientsModal-Upper-TextField-Container">
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Start/End Email Recipients (SBX users)</label> */}
                        <MultiSelectDropdownV2
                            dropdownOptions={usersStageStartEndEmail}
                            label="Stage Start/End Email Recipients (SBX users)"
                            onChange={(value: OptionType): void => {
                                onStageStartEndEmail(value);
                            }}
                            key={`my_unique_select_key__${JSON.stringify(selectedStageStartEndEmail)}`}
                            value={selectedStageStartEndEmail}
                        />
                    </Col>
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Start/End Email Recipients (non-SBX users)</label> */}
                        <div style={{ marginTop: '11px' }}>
                            <Chips
                                key={`stage-start-end-chips-${selectedStageStartEndEmailTextVal.map(c => c.email).join(',')}`}
                                chips={selectedStageStartEndEmailTextVal}
                                title="Stage Start/End Email Recipients (non-SBX users)"
                                placeholder="Add email"
                                save={changeStageStartEndEmailTextVal}
                                pattern={pattern}
                                required={false}
                                limit="500"
                                limitNotification={data => console.log('limit notification', data)}
                            ></Chips>
                        </div>
                    </Col>
                </Row>

                <Row className="ConfigureEmailRecipientsModal-Upper-TextField-Container">
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Start/End Email Recipients (SBX users)</label> */}
                        <MultiSelectDropdownV2
                            dropdownOptions={usersJobEventsEmail}
                            label="Job Events Email Recipients (SBX users)"
                            onChange={(value: OptionType): void => {
                                onJobEventEmail(value);
                            }}
                            key={`my_unique_select_key__${JSON.stringify(selectedJobEventsEmail)}`}
                            value={selectedJobEventsEmail}
                        />
                    </Col>
                    <Col md={6} className="ConfigureEmailRecipientsModal-Textfield">
                        {/* <label className="Label-InputTitle">Stage Start/End Email Recipients (non-SBX users)</label> */}
                        <div style={{ marginTop: '11px' }}>
                            <Chips
                                key={`job-events-chips-${selectedJobEventsEmailTextVal.map(c => c.email).join(',')}`}
                                chips={selectedJobEventsEmailTextVal}
                                title="Job Events Email Recipients (non-SBX users)"
                                placeholder="Add email"
                                save={changeJobEventEmailTextVal}
                                pattern={pattern}
                                required={false}
                                limit="500"
                                limitNotification={data => console.log('limit notification', data)}
                            ></Chips>
                        </div>
                    </Col>
                </Row>
            </div>

            <Modal.Footer>
                <div className="ConfigureEmailRecipientsModal-Footer">
                    <CustomButton title={'Cancel'} isBordered onClick={onClickClose}></CustomButton>
                    &nbsp;
                    <CustomButton title="Save" isDisabled={setSaveButtonStatus()} onClick={onSave}></CustomButton>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfigureEmailRecipientsModal;

